import * as React from "react";
import {
  AppointmentWorkflowStatus,
  ValidStateTransition,
  ValidStateTransitionAction,
  Workflow
} from "../types";
import { AppointmentState } from "../types";
import moment from "moment";
import { useRef, useState } from "react";
import useOnClickOutside from "../../../utils/useOnClickOutside";
import { Link } from "react-router-dom";
import { User } from "@sentry/browser";
import { useDispatch, useSelector } from "react-redux";
import { RootState, NavigationStep } from "../../types";
import { Organization } from "../../organizations/types";
import { setPatientAndAct } from "../../patient/actions";
import { Patient } from "../../patient/types";
import { get } from "../../../utils/fetch";
import env from "../../../utils/env";
import Tooltip from "../../../components/Tooltip";

type Props = {
  appointmentWorkflowStatus: AppointmentWorkflowStatus;
  updateAppointment: (
    queuedAppointment: AppointmentWorkflowStatus,
    selection: ValidStateTransition
  ) => void;
  selectedWorkflow: Workflow;
};

const AppointmentLineEntry: React.FunctionComponent<Props> = ({
  appointmentWorkflowStatus,
  updateAppointment,
  selectedWorkflow
}: Props) => {
  const dropdownMenu = useRef();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [patient, setPatient] = useState<Patient>();
  const inspectGraphModal = useRef();
  const [showInspectGraph, setShowInspectGraph] = useState<boolean>(false);

  const currentUser: User | null = useSelector(
    (state: RootState) => state.session.data.currentUser
  );
  const currentOrganization: Organization | null = useSelector(
    (state: RootState) => state.orgs.currentOrganization
  );
  const dispatch = useDispatch();

  useOnClickOutside(dropdownMenu, () => setShowMenu(false));
  useOnClickOutside(inspectGraphModal, () => setShowInspectGraph(false));

  const getClassNameForAction = (action: string): string => {
    switch (action) {
      case ValidStateTransitionAction.new:
        return "far fa-star";
      case ValidStateTransitionAction.cancel:
        return "fas fa-ban";
      case ValidStateTransitionAction.schedule:
        return "far fa-calendar-plus";
      case ValidStateTransitionAction.remind:
        return "far fa-comment-alt";
      case ValidStateTransitionAction.confirm:
        return "far fa-calendar-check";
      case ValidStateTransitionAction.reschedule:
        return "far fa-calendar-exclamation";
      case ValidStateTransitionAction.late_arrival:
        return "far fa-clock";
      case ValidStateTransitionAction.wait:
        return "fas fa-user-check";
      case ValidStateTransitionAction.request_entrance:
        return "far fa-comment-alt";
      case ValidStateTransitionAction.enter:
        return "far fa-clinic-medical";
      case ValidStateTransitionAction.complete:
        return "far fa-check complete";
      default:
        return "";
    }
  };

  const toggleMenu = async () => {
    setShowMenu(!showMenu);
    if (!patient) {
      getPatient();
    }
  };

  const getPatient = async () => {
    const url = `/api/organization/${currentOrganization?.id}/patients/${appointmentWorkflowStatus.appointment.patientId}/`;
    const resp = await get<Patient>(url);

    if (resp.data) {
      setPatient(resp.data);
    }
  };

  const dropdownAction = (
    appointmentWorkflowStatus: AppointmentWorkflowStatus,
    selection: ValidStateTransition
  ) => {
    updateAppointment(appointmentWorkflowStatus, selection);
    setShowMenu(false);
  };

  const navigate = (step: NavigationStep) => {
    patient && dispatch(setPatientAndAct(patient, step));
  };

  return (
    <div
      className={
        "flex entry " +
        (AppointmentState[appointmentWorkflowStatus.state] == AppointmentState.cancelled &&
          "cancelled")
      }
    >
      <div className="flex-2">{appointmentWorkflowStatus.appointment.patientDisplayName}</div>
      <div className="flex-1">
        {moment(appointmentWorkflowStatus.appointment.start).format("hh:mm a")}
      </div>
      <div className="flex-2">
        <span
          className={
            [
              AppointmentState.waiting,
              AppointmentState.entrance_requested,
              AppointmentState.entered
            ].includes(AppointmentState[appointmentWorkflowStatus.state])
              ? "bold"
              : ""
          }
        >
          {appointmentWorkflowStatus.stateLabel}
        </span>
        {showInspectGraph && (
          <div
            className="dialog-graph"
            role="button"
            tabIndex={0}
            onClick={() => setShowInspectGraph(false)}
          >
            <div className="dialog-graph-content">
              <h1>{selectedWorkflow.name} - Workflow Status</h1>
              <div className="mt-10 mb-20">
                <label>
                  <span className="bold">Patient: </span>
                  <span>{appointmentWorkflowStatus.appointment.patientDisplayName}</span>
                </label>
                <label>
                  <span className="bold">Time: </span>
                  <span>
                    {moment(appointmentWorkflowStatus.appointment.start).format("hh:mm a")}
                  </span>
                </label>
              </div>
              <div className="mt-10 mb-20">
                <img
                  className="graph-image"
                  alt="Graph"
                  src={`${env.PROJECT19_API_ROOT}api/organization/${
                    currentOrganization?.id
                  }/workflows/${selectedWorkflow.id}/appointments/${
                    appointmentWorkflowStatus.id
                  }/graph/?rnd=${moment().unix()}`}
                />
              </div>
            </div>
          </div>
        )}
        <div className="actions">
          {appointmentWorkflowStatus.validStateTransitions.map(t => {
            if (t.validForUi && t.uid != ValidStateTransitionAction.cancel)
              return (
                <button
                  className="link"
                  key={t.uid}
                  onClick={() => updateAppointment(appointmentWorkflowStatus, t)}
                >
                  {t.uid && <i className={getClassNameForAction(t.uid)}></i>}
                  {t.label}
                </button>
              );
          })}
        </div>
      </div>

      <div className="action-column">
        <div className="actionContainer" ref={dropdownMenu}>
          <button className="icon" onClick={toggleMenu}>
            <i className="fas fa-ellipsis-h icon pointer"></i>
          </button>

          {showMenu && (
            <div className="menu">
              {appointmentWorkflowStatus.validStateTransitions.map((t, index) => {
                if (
                  t.validForUi &&
                  t.uid === ValidStateTransitionAction.cancel &&
                  AppointmentState[appointmentWorkflowStatus.state] != AppointmentState.cancelled &&
                  AppointmentState[appointmentWorkflowStatus.state] != AppointmentState.completed
                ) {
                  return (
                    <div
                      className="link"
                      role="button"
                      tabIndex={index}
                      key={index}
                      onClick={() => dropdownAction(appointmentWorkflowStatus, t)}
                    >
                      Cancel Appointment
                    </div>
                  );
                }
              })}

              <Link to={`/patient/${appointmentWorkflowStatus.appointment.patientId}/`}>
                <div className="link" role="button" tabIndex={0}>
                  Update / View Patient
                </div>
              </Link>

              {!currentOrganization?.smsCommunicationDisabled && (
                <Link to={`/patient/${appointmentWorkflowStatus.appointment.patientId}/message`}>
                  <div className="link">Message Patient</div>
                </Link>
              )}
              {currentUser &&
                currentUser.featureFlags?.enableVideo &&
                !currentOrganization?.smsCommunicationDisabled && (
                  <div
                    className="link"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(NavigationStep.VideoChatSms)}
                  >
                    Text Video Visit Invite
                  </div>
                )}
              {currentUser &&
                currentUser.featureFlags?.enableVideo &&
                currentOrganization?.enableSendingEmail && (
                  <div
                    className={!patient?.email ? "link disabled" : "link"}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(NavigationStep.VideoChatEmail)}
                  >
                    Email Video Visit Invite
                  </div>
                )}
              {!currentOrganization?.smsCommunicationDisabled && (
                <Link
                  to={`/patient/${appointmentWorkflowStatus.appointment.patientId}/message/family/`}
                >
                  <div className="link">Message Family</div>
                </Link>
              )}
              {currentUser &&
                currentUser.featureFlags?.enableVideo &&
                !currentOrganization?.smsCommunicationDisabled && (
                  <Link to={`/patient/${patient?.id}/family/`}>
                    <div className="link" role="button">
                      Video Visit with Family
                    </div>
                  </Link>
                )}
              <div
                className="link"
                role="button"
                tabIndex={0}
                onClick={() => {
                  toggleMenu();
                  setShowInspectGraph(true);
                }}
              >
                Inspect Graph
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentLineEntry;
